/**
 * Controle centralizado do widget Sonax.
 * Única fonte de verdade para posicionar/mostrar/esconder o widget.
 *
 * Estrutura DOM Sonax (confirmada via DevTools):
 *
 * body
 *   └── div.SonaxWidget              ← outer wrapper (sem dimensões próprias)
 *        └── div.SonaxWidget          ← inner container (100x100 minimizado, overflow:hidden)
 *             ├── div#widget.widget    ← painel principal (90x90 minimizado)
 *             │    ├── div#content     ← container com header + principal-content + tab-bar
 *             │    │    └── div#principal-content  ← flex-row com PAINÉIS EXCLUSIVOS:
 *             │    │         ├── div#active-call       ← visível durante chamada
 *             │    │         ├── div#keyboard           ← visível para discador
 *             │    │         ├── div#transfer           ← transferência
 *             │    │         ├── div#history            ← histórico
 *             │    │         ├── div#contacts           ← contatos
 *             │    │         ├── div#add-contacts       ← adicionar contato
 *             │    │         └── div#settings           ← configurações
 *             │    └── div#icon        ← botão flutuante nativo (escondemos)
 *             ├── div#reciving-call    ← painel de chamada recebida
 *             └── div#active-call-minimized ← chamada ativa minimizada
 *
 * IMPORTANTE: Os painéis dentro de #principal-content são MUTUAMENTE EXCLUSIVOS.
 * O Sonax gerencia a troca entre eles. NÃO devemos forçar #keyboard visível
 * pois durante uma chamada o Sonax mostra #active-call e esconde #keyboard.
 * Forçar ambos visíveis causa layout "duplicado" (flex-row mostra lado a lado).
 */

// ============================================================================
// Gate de carregamento ("armado"). O script do webphone (e o registro do ramal
// no SIP) só é injetado quando o Sonax está ARMADO. Arma em: entrar na
// /discadora/operar, click-to-call (openWidgetAndCall), ou abrir o softphone do
// header. Desarma → o useSonaxWidget descarrega o script (ramal de-registra).
// Estado global via window flag + evento pra todas as instâncias do hook reagirem.
// ============================================================================
const ARM_EVENT = 'sonax:arm-changed';

export function isSonaxArmed(): boolean {
  return (window as any).__sonaxArmed === true;
}

export function armSonax(): void {
  if ((window as any).__sonaxArmed === true) return;
  (window as any).__sonaxArmed = true;
  window.dispatchEvent(new CustomEvent(ARM_EVENT, { detail: { armed: true } }));
}

export function disarmSonax(): void {
  if ((window as any).__sonaxArmed !== true) return;
  (window as any).__sonaxArmed = false;
  window.dispatchEvent(new CustomEvent(ARM_EVENT, { detail: { armed: false } }));
}

export function getOuterContainer(): HTMLElement | null {
  const all = document.querySelectorAll('div.SonaxWidget');
  for (const el of all) {
    if (el.parentElement?.tagName === 'BODY') return el as HTMLElement;
  }
  return all.length > 0 ? (all[0] as HTMLElement) : null;
}

export function getInnerContainer(): HTMLElement | null {
  const outer = getOuterContainer();
  if (!outer) return null;
  return outer.querySelector(':scope > div.SonaxWidget') as HTMLElement || outer;
}

export function expandWidget() {
  const outer = getOuterContainer();
  const inner = getInnerContainer();
  if (!outer || !inner) return;

  // OUTER: posiciona fixo no canto superior direito.
  // z-index: 99999 globalmente, mas DENTRO do focus mode usamos 40 para
  // ficar abaixo de overlays do Radix Dialog (z-50 padrão Tailwind/shadcn).
  // Sem isso, o widget sobrepõe o backdrop de modais como o PreflightDialog
  // e fica visualmente em cima do shadow.
  const inFocusMode = typeof document !== 'undefined' && document.body.classList.contains('focus-mode-active');
  outer.style.setProperty('display', 'block', 'important');
  outer.style.setProperty('position', 'fixed', 'important');
  outer.style.setProperty('z-index', inFocusMode ? '40' : '99999', 'important');
  outer.style.setProperty('right', '16px', 'important');
  outer.style.setProperty('top', '80px', 'important');
  outer.style.setProperty('left', 'auto', 'important');
  outer.style.setProperty('bottom', 'auto', 'important');
  outer.style.setProperty('pointer-events', 'auto', 'important');
  outer.setAttribute('data-sonax-visible', 'true');

  // INNER: remove a trava de 100x100, deixa o Sonax decidir o tamanho
  inner.style.setProperty('width', 'auto', 'important');
  inner.style.setProperty('height', 'auto', 'important');
  inner.style.setProperty('overflow', 'visible', 'important');
  inner.style.setProperty('pointer-events', 'auto', 'important');
  inner.setAttribute('data-sonax-visible', 'true');

  // Desfaz o `pointer-events: none !important` que o hideWidget() do SonaxWidgetContext
  // aplica em TODOS os descendentes. Sem isto o widget reabre visível porém morto: os
  // botões não respondem a clique. Antes só os call sites que chamavam showWidget() do
  // contexto se recuperavam disso; os demais (LeadProfile, Deals…) já sofriam do mesmo
  // problema em silêncio. Removemos a propriedade inline em vez de forçar 'auto' para
  // devolver o controle ao CSS do próprio widget.
  // (outer/inner já receberam pointer-events:auto acima; aqui só os descendentes)
  outer.querySelectorAll<HTMLElement>('*').forEach((child) => {
    child.style.removeProperty('pointer-events');
  });

  // O hideWidget() do contexto também aplica `display: none` inline em TODO elemento cujo
  // id/class casa com sonax|webphone — o que inclui o container INTERNO. Como expandWidget
  // só restaura o display do externo, o widget reabria com o outer visível e o inner
  // escondido: nada aparecia na tela. Limpamos exatamente o mesmo conjunto que o hide
  // marcou, e NÃO os demais descendentes: os painéis dentro de #principal-content
  // (#active-call, #keyboard, #history…) são mutuamente exclusivos e forçá-los visíveis
  // quebra o layout (ver cabeçalho deste arquivo).
  const hiddenBySelector = outer.querySelectorAll<HTMLElement>(
    '[id*="sonax" i], [class*="sonax" i], [id*="webphone" i], [class*="webphone" i]',
  );
  hiddenBySelector.forEach((el) => el.style.removeProperty('display'));
  inner.style.removeProperty('display');

  // Esconde #icon nativo (botão flutuante azul)
  const icon = inner.querySelector('#icon') as HTMLElement;
  if (icon) icon.style.setProperty('display', 'none', 'important');

  // Esconde botão nativo de fechar
  const closeBtn = inner.querySelector('#close-widget') as HTMLElement;
  if (closeBtn) closeBtn.style.setProperty('display', 'none', 'important');

  // #widget: garante visibilidade sem forçar dimensões
  const widgetPanel = inner.querySelector('#widget') as HTMLElement;
  if (widgetPanel) {
    widgetPanel.style.setProperty('transform', 'none', 'important');
    widgetPanel.style.setProperty('opacity', '1', 'important');
    widgetPanel.style.setProperty('visibility', 'visible', 'important');
    widgetPanel.style.setProperty('pointer-events', 'auto', 'important');
  }

  // Abre o conteudo interno do widget Sonax (#content = header + principal-content + tab-bar)
  // O Sonax minimizado esconde #content — precisamos forçar visibilidade
  const content = inner.querySelector('#content') as HTMLElement;
  if (content) {
    content.style.setProperty('display', 'flex', 'important');
    content.style.setProperty('visibility', 'visible', 'important');
    content.style.setProperty('opacity', '1', 'important');
  }

  // (Removido) Forçar dimensões em #reciving-call e #active-call-minimized
  // estava interferindo com o ciclo de vida interno do Sonax. Próxima
  // tentativa precisa do DOM real do widget durante chamada recebida.

  // NÃO forçamos #keyboard visível aqui — os painéis dentro de #principal-content
  // (#keyboard, #active-call, #history, etc.) são mutuamente exclusivos e gerenciados
  // pelo Sonax internamente. Forçar #keyboard durante uma chamada causa layout duplicado.
}

export function collapseWidget() {
  const outer = getOuterContainer();
  const inner = getInnerContainer();
  if (!outer) return;

  outer.style.setProperty('display', 'none', 'important');
  outer.style.setProperty('pointer-events', 'none', 'important');
  outer.removeAttribute('data-sonax-visible');

  if (inner) {
    inner.style.setProperty('overflow', 'hidden', 'important');
    inner.removeAttribute('data-sonax-visible');
  }
}

// ============================================================================
// Controle programático de chamadas (modo /discadora/operar — widget escondido).
// Dirigimos os botões NATIVOS do widget via .click() — funciona mesmo com o
// widget display:none (click() sintético não depende de visibilidade).
// Os seletores são heurísticos (sem doc do DOM interno): 1º por atributos
// (id/class/src/alt/title), 2º por cor de fundo (botão verde = atender,
// vermelho = desligar). Logamos o alvo clicado pra validação ao vivo.
// ============================================================================

const ANSWER_RX = /atend|answer|accept|aceit/i;
const REJECT_RX = /recus|declin|reject|hang|deslig|encerr|end[-_]?call|finish/i;

function attrBlob(el: HTMLElement): string {
  return [
    el.id,
    typeof el.className === 'string' ? el.className : '',
    el.getAttribute('src'),
    el.getAttribute('alt'),
    el.getAttribute('title'),
    el.getAttribute('aria-label'),
  ]
    .filter(Boolean)
    .join(' ');
}

/** Sobe até um ancestral clicável (button/a/[onclick]/role=button) dentro do painel. */
function clickableAncestor(el: HTMLElement, boundary: HTMLElement): HTMLElement {
  let cur: HTMLElement | null = el;
  while (cur && cur !== boundary) {
    if (
      cur.tagName === 'BUTTON' ||
      cur.tagName === 'A' ||
      cur.hasAttribute('onclick') ||
      cur.getAttribute('role') === 'button'
    ) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return el;
}

function parseRgb(color: string): [number, number, number] | null {
  const m = color.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

function findCallButton(panel: HTMLElement, kind: 'answer' | 'reject'): HTMLElement | null {
  const rx = kind === 'answer' ? ANSWER_RX : REJECT_RX;
  const all = Array.from(panel.querySelectorAll<HTMLElement>('*'));

  // 1) Match por atributos (id/class/src/alt/title/aria-label)
  for (const el of all) {
    if (rx.test(attrBlob(el))) return clickableAncestor(el, panel);
  }

  // 2) Fallback por cor de fundo: verde = atender, vermelho = recusar/desligar
  for (const el of all) {
    const rgb = parseRgb(window.getComputedStyle(el).backgroundColor);
    if (!rgb) continue;
    const [r, g, b] = rgb;
    const isGreen = g > r + 40 && g > b + 40;
    const isRed = r > g + 40 && r > b + 40;
    if ((kind === 'answer' && isGreen) || (kind === 'reject' && isRed)) {
      return clickableAncestor(el, panel);
    }
  }
  return null;
}

function clickIn(panelSelector: string, kind: 'answer' | 'reject'): boolean {
  const panel = document.querySelector(panelSelector) as HTMLElement | null;
  if (!panel) return false;
  const btn = findCallButton(panel, kind);
  if (!btn) {
    console.warn(`[sonaxWidgetControl] botão "${kind}" não encontrado em ${panelSelector}`);
    return false;
  }
  console.log(`[sonaxWidgetControl] click "${kind}" em ${panelSelector}:`, btn.tagName, btn.id || btn.className);
  btn.click();
  return true;
}

/**
 * Handlers GLOBAIS do widget (confirmados no fonte de webphone2.sonax.cloud/import?path=content.html):
 *   acceptCall() — atender · refuseCall() — recusar · finishCall() — desligar a ativa.
 * Os botões são <img onclick="...."> com src accept.png / refuse.png / down.png.
 */
function callGlobal(fnName: string): boolean {
  const fn = (window as any)[fnName];
  if (typeof fn !== 'function') return false;
  try {
    console.log(`[sonaxWidgetControl] chamando ${fnName}() global do widget`);
    fn();
    return true;
  } catch (e) {
    console.warn(`[sonaxWidgetControl] ${fnName}() lançou:`, e);
    return false;
  }
}

function clickImgBySrc(srcPart: string): boolean {
  const img = document.querySelector<HTMLElement>(`img[src*="${srcPart}"]`);
  if (!img) return false;
  console.log(`[sonaxWidgetControl] click em img[src*="${srcPart}"]`);
  img.click();
  return true;
}

/** Atende a chamada que está tocando. */
export function answerIncomingCall(): boolean {
  return callGlobal('acceptCall') || clickImgBySrc('accept.png') || clickIn('#reciving-call', 'answer');
}

/** Recusa a chamada que está tocando. */
export function declineIncomingCall(): boolean {
  return callGlobal('refuseCall') || clickImgBySrc('refuse.png') || clickIn('#reciving-call', 'reject');
}

/** Desliga a chamada ativa. */
export function hangupActiveCall(): boolean {
  return (
    callGlobal('finishCall') ||
    clickImgBySrc('active-call/down.png') ||
    clickIn('#active-call', 'reject') ||
    clickIn('#active-call-minimized', 'reject') ||
    clickIn('#reciving-call', 'reject')
  );
}
